import { Box } from '@dealstack/frontend/shared/components/Box/Box';
import { ConfirmationModal } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModal';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { TagText, TagPair } from '@dealstack/frontend/shared/components/TagPair/TagPair';
import { rem } from '@mantine/core';
import { useDeal } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/Deal/useDeal';
import { SummaryDealTct } from '@dealstack/shared/lib/generated';
import { flexRender, Row } from '@tanstack/react-table';
import { memo } from 'react';
import styled from 'styled-components';

interface Props {
	refetchMipData: () => void;
}

const Deal = ({ refetchMipData }: Props) => {
	const {
		isDataLoading,
		deal,
		tableContainerRef,
		rows,
		rowVirtualizer,
		tanstackTable,
		selectedDeal,
		setSelectedDeal,
		reParentDeal,
	} = useDeal(refetchMipData);

	return (
		<div style={{ position: 'relative' }}>
			{isDataLoading ? <InlineLoader /> : null}
			<DealBox>
				<TextWrapper>
					<TagPair title="Current Deal ID">
						<TagText>{deal?.id}</TagText>
					</TagPair>
					<TagPair title="Current Deal name">
						<TagText>{deal?.name}</TagText>
					</TagPair>
					<TagPair title="Current Deal Target name">
						<TagText>{deal?.targetName}</TagText>
					</TagPair>
				</TextWrapper>
				<div>
					<Text bold elipsis>
						Re-parent investment programme to a different deal
					</Text>
					<TableWrapper>
						{isDataLoading ? <InlineLoader /> : null}
						<table className="table" ref={tableContainerRef}>
							<thead>
								{tanstackTable.getHeaderGroups().map((headerGroup) => (
									<tr key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<th
													key={header.id}
													colSpan={header.colSpan}
													style={{ width: header.getSize() }}
												>
													{header.isPlaceholder ? null : (
														<div
															{...{
																className:
																	header.column.getCanSort()
																		? 'cursor-pointer clickAnimation select-none'
																		: '',
																onClick:
																	header.column.getToggleSortingHandler(),
															}}
														>
															{flexRender(
																header.column.columnDef.header,
																header.getContext()
															)}
															{{
																asc: ' 🔼',
																desc: ' 🔽',
															}[
																header.column.getIsSorted() as string
															] ?? null}
														</div>
													)}
												</th>
											);
										})}
									</tr>
								))}
							</thead>
							<tbody>
								{rowVirtualizer.getVirtualItems().map((virtualRow) => {
									const row = rows[virtualRow.index] as Row<SummaryDealTct>;
									return (
										<tr key={row.id}>
											{row.getVisibleCells().map((cell) => {
												if (cell.row.original.id === deal?.id) {
													return null;
												}
												return (
													<td key={cell.id}>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext()
														)}
													</td>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						</table>
					</TableWrapper>
					<br />
					<div>{tanstackTable.getRowModel().rows.length - 1} Rows</div>
					{selectedDeal?.id && (
						<ConfirmationModal
							title="Re-parent this investment programme to a different deal"
							subtitle={`Are you sure you want to re-parent this investment programme from ${deal?.name} to ${selectedDeal.name}?`}
							confirmationButtonText="Re-parent"
							onClose={() => {
								setSelectedDeal(null);
							}}
							onSubmit={reParentDeal}
						/>
					)}
				</div>
			</DealBox>
		</div>
	);
};

const TableWrapper = styled.div`
	border: 1px solid lightgray;
	overflow: auto;
	max-height: 40rem;
	position: relative;

	table {
		border-collapse: collapse;
		border-spacing: 0;
		font-family: arial, sans-serif;
		table-layout: fixed;
		width: 100%;
	}

	thead {
		background: white !important;
		margin: 0;
		position: sticky;
		top: 0;
		opacity: 1;
		z-index: 1;
	}

	th {
		border-bottom: 1px solid lightgray;
		border-right: 1px solid lightgray;
		padding: ${rem(2)} ${rem(4)};
		text-align: left;
	}

	td {
		padding: ${rem(10)} ${rem(6)};
	}

	button {
		:focus {
			border: none;
		}
	}
`;

const DealBox = styled(Box)`
	display: flex;
	flex-direction: column;
`;

const TextWrapper = styled.div`
	display: flex;
	align-items: top;
	& > * {
		margin-right: 2rem;
	}
	margin-bottom: 2rem;
`;
const _DealExport = memo(Deal);

export { _DealExport as Deal };
